import { PvPerformanceResponse, PvPerformanceWindowKey } from '@core/models';
import { PerformanceTrendPoint } from './performance-ratio.component';
import { PerformanceRatios } from './performance-verdict';

/**
 * Translates the PV performance endpoint's payload into the two inputs the card takes.
 *
 * The card knows three named windows; the endpoint keys them by their length in days. That
 * mapping is the whole reason this file exists, and it is the only place that knows it.
 *
 * A missing response maps to three `null` ratios and an empty trend rather than to an error
 * state. `performanceVerdict` already turns that into "not enough data", which is the honest
 * reading: no document, too few valid days and a meter the endpoint refuses are all the same
 * thing to a viewer - there is no ratio to show.
 */
const WINDOW_KEYS: Record<keyof PerformanceRatios, PvPerformanceWindowKey> = {
  lastCompleteDay: '1',
  sevenDay: '7',
  thirtyDay: '30',
};

export function pvPerformanceRatios(response: PvPerformanceResponse | null): PerformanceRatios {
  return {
    lastCompleteDay: windowRatio(response, WINDOW_KEYS.lastCompleteDay),
    sevenDay: windowRatio(response, WINDOW_KEYS.sevenDay),
    thirtyDay: windowRatio(response, WINDOW_KEYS.thirtyDay),
  };
}

export function pvPerformanceTrend(
  response: PvPerformanceResponse | null,
): PerformanceTrendPoint[] {
  return (response?.trend7d ?? []).map(point => ({ date: point.day, ratio: point.ratio }));
}

// `?? null` rather than a bare read: a window the backend did not serve at all must land on
// the same `null` as one it served with an undefined ratio, never on `undefined`, which the
// verdict helper does not accept.
function windowRatio(
  response: PvPerformanceResponse | null,
  key: PvPerformanceWindowKey,
): number | null {
  return response?.windows?.[key]?.ratio ?? null;
}
